import type { JSX } from 'react';

import { isError, isLoading } from 'src/app/shared/utils/async';

import { ErrorState, ListHeading } from '../../shared/components';
import { LoadingCardsSkeleton } from '../../shared/components/LoadingCardsSkeleton';
import type { SuggestedPetitionSourceLocation } from '../../shared/types';
import { getTitleI18n } from '../../shared/utils/getTitleI18n';

import { SuggestedPetitionsList } from './components/SuggestedPetitionsList';
import type { Channel } from './hooks/useSuggestedPetitions';
import { useSuggestedPetitions } from './hooks/useSuggestedPetitions';

type Props = {
	petitionSlugOrId?: string;
	channel: Channel;
	sourceLocation: SuggestedPetitionSourceLocation;
};

export type { Channel };

export function SuggestedPetitions({ petitionSlugOrId, channel, sourceLocation }: Props): JSX.Element | null {
	const {
		data: { petitionsState },
	} = useSuggestedPetitions({ petitionSlugOrId, channel });

	if (isLoading(petitionsState)) {
		return (
			<div data-nosnippet>
				<ListHeading title={getTitleI18n([])} />
				<LoadingCardsSkeleton />
			</div>
		);
	}

	if (isError(petitionsState)) {
		return (
			<div data-nosnippet>
				<ListHeading title={getTitleI18n([])} />
				<ErrorState />
			</div>
		);
	}

	if (!petitionsState.petitions.length) {
		// TODO display an "empty" message?
		return null;
	}

	return (
		// https://iloveseo.com/seo/use-data-nosnippet-attribute-to-remove-text-in-google-serp-snippet/
		<div data-nosnippet>
			<ListHeading title={getTitleI18n(petitionsState.petitions)} />
			<SuggestedPetitionsList
				petitions={petitionsState.petitions}
				parentPetitionId={petitionsState.parentPetitionId}
				sourceLocation={sourceLocation}
			/>
		</div>
	);
}
